import {
  Autocomplete,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import {
  createFundPayments,
  getPaymentSuggestions,
  updateFundPayment,
} from 'lib/api/funds';
import WarningDeleteDialog from 'lib/common/WarningDeleteDialog';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import { errorHandler } from 'lib/functions/errors';
import { formatMoney, formatMoneyBack } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

const NewFundPayment = (props: any) => {
  const { open, setOpen, setSuccess, setUp, fund, edit, fundPayment } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<any>([]);
  const [business, setBusiness] = useState<any>(null);
  const [documents, setDocuments] = useState<any>([]);
  const [date, setDate] = useState<any>(new Date());
  const [openFundPendingReviewDialog, setOpenFundPendingReviewDialog] =
    useState(false);
  const [fundPendingReviewMessages, setFundPendingReviewMessages] = useState<
    string[]
  >([]);

  const validParams = () => {
    if (!documents.length || !business) {
      return false;
    }
    if (!documents.every((doc: any) => doc.amount)) {
      return false;
    }
    return true;
  };

  const handleChangeBusiness = (event: any, newValue: any) => {
    setBusiness(newValue);
  };

  const handleChangeDocument = (event: any, newValue: any) => {
    setDocuments(newValue);
  };

  const handleSubmit = async () => {
    if (!validParams()) {
      return;
    }
    setLoading(true);
    try {
      const payments = documents.map((doc: any) => ({
        documentId: doc.id,
        businessIdentifier: business.identifier,
        date: new Date(date),
        amount: doc.amount,
        fundId: fund.id,
      }));
      if (edit) {
        await updateFundPayment(dispatch, {
          id: fund.id,
          paymentId: fundPayment.id,
          payment: payments[0],
        });
        setOpen(false);
        setSuccess('Pago guardado correctamente');
      } else {
        const newPayload = {
          payments,
        };
        const response = await createFundPayments(dispatch, newPayload);

        if (response?.messages && response.messages.length > 0) {
          const messages = response.messages.map(
            (msg: { message: string }) => msg.message,
          );
          setFundPendingReviewMessages(messages);
          setOpenFundPendingReviewDialog(true);
          setOpen(false);
        } else {
          setOpen(false);
          setSuccess('Pago guardado correctamente');
        }
      }
      await setUp();
    } catch (error) {
      errorHandler(dispatch, error);
    }
    setLoading(false);
  };

  const setUpSuggestions = async () => {
    setLoading(true);
    try {
      const response = await getPaymentSuggestions(dispatch, { id: fund.id });
      setSuggestions(response);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    void setUpSuggestions();
  }, []);

  return (
    <div>
      <Dialog
        fullWidth
        maxWidth="md"
        open={open}
        onClose={() => setOpen(false)}
      >
        <HeaderWithActions
          title={edit ? `Editar pago` : `Nuevo pago`}
          type="dialog"
        />
        {loading ? (
          <CenteredCircularProgress type="dialog" />
        ) : (
          <DialogContent>
            <Grid container spacing={0} padding={'10px 0px'}>
              <Grid item xs={5} padding="0px 16px">
                <Typography variant="body1">
                  Ingrese los datos del nuevo pago del fondo <b>{fund.name}</b>
                </Typography>
                <Autocomplete
                  style={{ margin: '8px 0px' }}
                  options={suggestions.businesses}
                  getOptionLabel={(option: any) =>
                    `${option.name} | ${option.identifier}`
                  }
                  renderInput={(params: any) => (
                    <TextField {...params} label="De quíen fue el pago?" />
                  )}
                  onChange={handleChangeBusiness}
                  fullWidth
                />
                <TextField
                  label="Monto"
                  fullWidth
                  type="date"
                  margin="dense"
                  value={date}
                  onChange={(e) => {
                    setDate(e.target.value);
                  }}
                />
                <Autocomplete
                  options={suggestions.documents || []}
                  style={{ margin: '8px 0px' }}
                  getOptionLabel={(option: any) =>
                    `${option.folio} | ${option.businessName} | ${option.stakeholderName}`
                  }
                  renderInput={(params: any) => (
                    <TextField {...params} label="Documentos del pago" />
                  )}
                  renderTags={(value: any) => {
                    return value.map((option: any, index: any) => (
                      <Chip
                        key={index}
                        style={{ margin: '4px' }}
                        label={option.folio}
                      />
                    ));
                  }}
                  defaultValue={[]}
                  multiple
                  onChange={handleChangeDocument}
                  fullWidth
                  disabled={!business}
                />
              </Grid>
              <Grid
                item
                xs={7}
                padding="0px 16px"
                style={{ borderLeft: '1px solid grey' }}
              >
                <Typography variant="body1">
                  Asigne los montos a los documentos seleccionados
                </Typography>

                <div style={{ padding: '10px 0px' }}>
                  {documents.map((document: any, index: any) => (
                    <Grid
                      container
                      key={index}
                      style={{ padding: '2px 0px' }}
                      alignItems="center"
                    >
                      <Grid item xs={4}>
                        <Typography variant="body1">
                          - {document.folio}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body1">
                          {formatMoney(document.amount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          label="Monto notificado"
                          fullWidth
                          margin="dense"
                          value={formatMoney(document.amount)}
                          onChange={(e) => {
                            const nonFomatted = formatMoneyBack(e.target.value);
                            const newDocuments = documents.map((doc: any) => {
                              if (doc.id === document.id) {
                                return { ...doc, amount: nonFomatted };
                              }
                              return doc;
                            });
                            setDocuments(newDocuments);
                          }}
                        />
                      </Grid>
                    </Grid>
                  ))}
                </div>
              </Grid>
            </Grid>
          </DialogContent>
        )}
        <DialogActions>
          <CustomButton onClick={() => setOpen(false)} color="secondary">
            Salir
          </CustomButton>
          <CustomButton onClick={() => handleSubmit()} color="primary">
            Guardar
          </CustomButton>
        </DialogActions>
      </Dialog>
      {openFundPendingReviewDialog && (
        <WarningDeleteDialog
          open={openFundPendingReviewDialog}
          setOpen={setOpenFundPendingReviewDialog}
          title="Pago guardado correctamente"
          warningMessages={fundPendingReviewMessages}
          deleteItem={async () => ({ displayMessage: '' })}
          requireConfirmation={false}
          mode="info"
          severity="success"
        />
      )}
    </div>
  );
};

export default NewFundPayment;
